// Document-level painting: tool + settings + points -> new document. Pure — no React, no DOM.
// The editor's reducer is a thin wrapper over these, so the self-test exercises the same code the
// UI runs.

import type { Color, Point, Tool } from './types';
import type { SkinModel } from './skinMap';
import { type SkinDocument, getActiveLayer, setLayerPixels } from './layers';
import { TOOL_REGISTRY, type StrokeToolDef } from './tools';
import { applyPixelOp, expandStroke, withMirror, floodFillPoints, faceBoundsAt, inBounds, type Bounds } from './paint';

export interface PaintSettings {
  color: Color;
  brushSize: number;
  model: SkinModel;
  mirror: boolean;
  /** Shading strength, 0..1 (OKLab lightness). Only the shading tools read it. Default 0.1. */
  strength?: number;
  /** Per-stroke seed for the Noise brush. Default 1. */
  seed?: number;
}

function writeActive(
  doc: SkinDocument, points: readonly Point[], op: StrokeToolDef['pixelOp'], s: PaintSettings, source?: Uint8ClampedArray,
): SkinDocument {
  const layer = getActiveLayer(doc);
  if (layer.locked || points.length === 0) return doc;
  const ctx = { color: s.color, strength: s.strength ?? 0.1, seed: s.seed ?? 1, model: s.model, mirror: s.mirror };
  const next = applyPixelOp(layer.pixels, points, (src, x, y) => op(src, { ...ctx, x, y }), source ?? layer.pixels);
  return setLayerPixels(doc, layer.id, next);
}

/**
 * Paint brush centers with a STROKE tool. `centers` should already be gap-free (linePoints between
 * successive pointer samples). Returns the same doc when nothing changed or the layer is locked.
 *
 * `strokeBase` is the document as it was when the stroke began. The tool reads each pixel from the
 * active layer THERE (when it is the same layer), so every pixel's result is a function of the
 * stroke's starting pixel: re-painting a pixel within one stroke cannot compound a shading brush.
 * Pencil and eraser ignore what they read, so for them this changes nothing.
 */
export function paintStroke(
  doc: SkinDocument, tool: Tool, centers: readonly Point[], s: PaintSettings, strokeBase?: SkinDocument | null,
): SkinDocument {
  const def = TOOL_REGISTRY[tool];
  if (def.kind !== 'stroke') return doc;
  const active = getActiveLayer(doc);
  const baseLayer = strokeBase ? strokeBase.layers.find(l => l.id === active.id) : undefined;
  return writeActive(doc, expandStroke(centers, s.brushSize, s.model, s.mirror), def.pixelOp, s, baseLayer?.pixels);
}

/**
 * Draw a SHAPE tool from (x0, y0) to (x1, y1) in the primary color. Shapes are 1px wide. `clip`
 * restricts the result (the Body Parts view clips to the face being drawn on); mirror applies after.
 */
export function paintShape(
  doc: SkinDocument, tool: Tool, x0: number, y0: number, x1: number, y1: number, s: PaintSettings, clip?: Bounds,
): SkinDocument {
  const def = TOOL_REGISTRY[tool];
  if (def.kind !== 'shape') return doc;
  const pts = def.points(x0, y0, x1, y1).filter(p => inBounds(p) && (!clip || inBounds(p, clip)));
  return writeActive(doc, withMirror(pts, s.model, s.mirror), (_src, ctx) => ctx.color, s);
}

/**
 * Bucket fill at (x, y), bounded by the face under the click and read from the ACTIVE layer. In
 * mirror mode the filled area's mirror image is painted too (a symmetric result, rather than a
 * second flood fill that would follow whatever happens to be on the other side).
 */
export function paintFill(doc: SkinDocument, x: number, y: number, s: PaintSettings): SkinDocument {
  const layer = getActiveLayer(doc);
  if (layer.locked) return doc;
  const pts = floodFillPoints(layer.pixels, x, y, s.color, faceBoundsAt(x, y, s.model));
  return writeActive(doc, withMirror(pts, s.model, s.mirror), (_src, ctx) => ctx.color, s);
}
